import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { outlineBtnCls } from './styles';
import FilterSelect, { SelectOption } from './FilterSelect';
import FilterMultiSelect from './FilterMultiSelect';
import { Z } from '../ui';

interface SelectFilterDef {
  type?: 'select';
  key: string;
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (v: string) => void;
  title?: string;
}

interface MultiSelectFilterDef {
  type: 'multiselect';
  key: string;
  label: string;
  values: string[];
  options: SelectOption[];
  onChange: (v: string[]) => void;
  title?: string;
}

interface CustomFilterDef {
  type: 'custom';
  key: string;
  label: string;
  /** Arbitrary control(s) for filters that aren't a single select (e.g. the answer filter). */
  node: ReactNode;
}

export type FilterDef = SelectFilterDef | MultiSelectFilterDef | CustomFilterDef;

interface Props {
  filters: FilterDef[];
  /** Number of these filters currently applied — drives the count badge. */
  activeCount: number;
  /** Button label (defaults to "Add filter"). */
  label?: string;
}

/**
 * "Add filter" popover holding the secondary filters as labeled fields. This is
 * the Option A overflow + Option B affordance in one: a single button (with an
 * active-count badge) that reveals the long-tail filters, while applied values
 * surface as dismissible chips via <ActiveFilters>.
 */
export default function AddFilterMenu({ filters, activeCount, label = 'Add filter' }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Escape (bubble phase — the kit's ConfirmDialog owns the capture phase and
  // stops it there, so a confirm raised from inside this menu closes itself
  // first, never this menu and the confirm on the same keystroke) + outside
  // click, both only while the menu is open.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className={outlineBtnCls(activeCount > 0)}
      >
        <SlidersHorizontal className="h-4 w-4" />
        {label}
        {activeCount > 0 && (
          <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary-600 px-1.5 text-xs font-medium text-white">
            {activeCount}
          </span>
        )}
      </button>
      {/* Always mounted, toggled with `hidden` (D-2 house rule) — so a
          half-filled custom filter node (e.g. the owner multiselect + its
          "include unassigned" checkbox) survives closing and reopening the
          menu instead of resetting. No filter node here fetches on mount —
          FilterSelect/FilterMultiSelect only render already-loaded options —
          so mounting the panel early adds no new network activity. */}
      <div
        role="dialog"
        aria-label={label}
        className={`absolute right-0 ${Z.menuSheet} mt-2 w-80 max-w-[calc(100vw-2rem)] space-y-3 rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800 max-sm:fixed max-sm:inset-x-3 max-sm:bottom-[calc(4.5rem+env(safe-area-inset-bottom))] max-sm:top-auto max-sm:mt-0 max-sm:w-auto max-sm:max-h-[65vh] max-sm:overflow-y-auto max-sm:shadow-xl ${open ? '' : 'hidden'}`}
      >
        {filters.map((f) => (
          <div key={f.key}>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">{f.label}</label>
            {f.type === 'custom' ? (
              f.node
            ) : f.type === 'multiselect' ? (
              <FilterMultiSelect values={f.values} onChange={f.onChange} options={f.options} label={f.label} title={f.title} />
            ) : (
              <FilterSelect value={f.value} onChange={f.onChange} options={f.options} label={f.label} title={f.title} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
